/** Client-Helfer — spiegelt apps/api/src/game/mapTactical.ts + hydrantPressure.ts */

import { haversineMeters, interpolateRoute, type LatLng } from "./geo";
import { parseIncidentSupport } from "./incidentSupport";

export type MapTacticalMode =
  | "pursuit"
  | "staging"
  | "rd_staging"
  | "lage"
  | "water"
  | "water_rescue"
  | "barrier";
export type PlacementType =
  | "nail_strip"
  | "road_block"
  | "unit_post"
  | "helo_corridor"
  | "staging_point"
  | "sector_boundary"
  | "evacuation_zone"
  | "patient_staging"
  | "traffic_control"
  | "hydrant_pick"
  | "suction_point";

export type MapTacticalPlacement = {
  id: string;
  type: PlacementType;
  lat: number;
  lng: number;
  vehicleId?: string;
};

export type MapTacticalState = {
  mode: MapTacticalMode;
  phase: string;
  suspectProgress: number;
  route: LatLng[];
  escapeZone?: { lat: number; lng: number; radiusM: number };
  placements: MapTacticalPlacement[];
  resolved: boolean;
  outcome?: string;
  heloActive?: boolean;
  stagingUnlocked?: boolean;
  label?: string;
  maxPlacements?: Partial<Record<PlacementType, number>>;
};

export const HYDRANT_PRESSURE_HINT =
  "Hydrantennetz: Druckmangel — Saugstelle oder TLF-Pendel nötig";

export function parseHydrantPressureFail(supportJson?: string | null): boolean {
  return parseIncidentSupport(supportJson).hydrantPressureFail === true;
}

export function parseWaterHold(supportJson?: string | null): boolean {
  const raw = parseIncidentSupport(supportJson);
  return raw.waterHold === true && raw.waterResolved !== true && raw.pendel !== true;
}

export function parseTacticFlags(supportJson?: string | null) {
  const raw = parseIncidentSupport(supportJson);
  return {
    pending: raw.tacticPending === true,
    resolved: raw.tacticResolved === true,
    templateId: typeof raw.tacticTemplateId === "string" ? raw.tacticTemplateId : null,
    triageStagingLinked: raw.triageStagingLinked === true,
    tacticMapHint: typeof raw.tacticMapHint === "string" ? raw.tacticMapHint : null,
  };
}

const PURSUIT_TEMPLATES = new Set([
  "pol_helo_pursuit",
  "ghost_driver",
  "pol_highway_pursuit",
  "highway_multi_crash",
]);
const PURSUIT_TITLE = /Verfolg|Fahndung|Geister|Flucht/i;
const WATER_TEMPLATES = new Set(["lake_vegetation_fire"]);
const WATER_TITLE = /brand.*see|vegetation.*see|wasserknapp|saugstelle/i;
const WATER_RESCUE_TEMPLATES = new Set(["water_rescue_thw"]);
const WATER_RESCUE_TITLE = /Wasserrettung|Ufer|Person im Wasser|THW.*Wasser/i;
const RD_STAGING_TEMPLATES = new Set(["rd_manv_bus"]);
const RD_STAGING_TITLE = /MANV|Massenanfall|Patientenablage|Patienten-Sammel/i;
const BARRIER_TEMPLATES = new Set([
  "motorway_crash",
  "motorway_truck_fire",
  "fuel_station_spill",
  "traffic_accident",
  "traffic_accident_damage",
  "traffic_injury",
]);
const BARRIER_TITLE = /VU|Verkehrsunfall|BAB|Autobahn|Kraftstoff|Ölspur|Sperr/i;
const LAGE_TITLE =
  /Großlage|Lagezentrum|Evakuierung|Scheune|Banküberfall|Volksfest|Schulbrand/i;

export const MODE_LABELS: Record<MapTacticalMode, string> = {
  pursuit: "Verfolgung",
  staging: "Großlage",
  rd_staging: "MANV / RD",
  lage: "Lageführung",
  water: "Wasserversorgung",
  water_rescue: "Wasserrettung (THW)",
  barrier: "Absperrung",
};

const DEFAULT_MAX: Record<MapTacticalMode, Partial<Record<PlacementType, number>>> = {
  pursuit: { nail_strip: 2, road_block: 1, traffic_control: 1, unit_post: 3, helo_corridor: 1 },
  staging: { staging_point: 1, sector_boundary: 0, evacuation_zone: 1 },
  rd_staging: { staging_point: 1, sector_boundary: 2, patient_staging: 3 },
  lage: {
    staging_point: 1,
    sector_boundary: 2,
    road_block: 2,
    evacuation_zone: 1,
    unit_post: 1,
  },
  water: { hydrant_pick: 1, suction_point: 1 },
  water_rescue: { staging_point: 1, sector_boundary: 1, suction_point: 1 },
  barrier: { road_block: 3, traffic_control: 2, unit_post: 2 },
};

export function toolsForMapTactical(
  mode: MapTacticalMode,
  maxPlacements?: Partial<Record<PlacementType, number>>,
): PlacementType[] {
  const max = { ...DEFAULT_MAX[mode], ...maxPlacements };
  const order: PlacementType[] =
    mode === "pursuit"
      ? ["nail_strip", "road_block", "traffic_control", "unit_post", "helo_corridor"]
      : mode === "staging"
        ? ["staging_point", "sector_boundary", "evacuation_zone"]
        : mode === "rd_staging"
          ? ["staging_point", "sector_boundary", "patient_staging"]
          : mode === "lage"
            ? ["staging_point", "sector_boundary", "road_block", "evacuation_zone", "unit_post"]
            : mode === "water"
              ? ["hydrant_pick", "suction_point"]
              : mode === "water_rescue"
                ? ["staging_point", "sector_boundary", "suction_point"]
                : ["road_block", "traffic_control", "unit_post"];
  return order.filter((t) => (max[t] ?? 0) > 0);
}

export const PLACEMENT_LABELS: Record<PlacementType, string> = {
  nail_strip: "Nagelstreifen",
  road_block: "Sperrstelle",
  unit_post: "Einheit postieren",
  helo_corridor: "Heli-Sichtkontakt",
  staging_point: "Kräfte-Sammelstelle",
  sector_boundary: "Abschnitt markieren",
  evacuation_zone: "Evakuierungsbereich",
  patient_staging: "Patienten-Sammelstelle",
  traffic_control: "Verkehrskontrollpunkt",
  hydrant_pick: "Löschwasserbezug",
  suction_point: "Saugstelle",
};

export const PLACEMENT_ICONS: Record<PlacementType, string> = {
  nail_strip: "⛓",
  road_block: "🚧",
  unit_post: "🚓",
  helo_corridor: "🚁",
  staging_point: "📍",
  sector_boundary: "▥",
  evacuation_zone: "⭕",
  patient_staging: "🏥",
  traffic_control: "🛑",
  hydrant_pick: "🚰",
  suction_point: "💧",
};

const MODES: MapTacticalMode[] = [
  "pursuit",
  "staging",
  "rd_staging",
  "lage",
  "water",
  "water_rescue",
  "barrier",
];

export function parseMapTactical(supportJson?: string | null): MapTacticalState | null {
  try {
    const raw = parseIncidentSupport(supportJson);
    const mt = raw.mapTactical;
    if (!mt || typeof mt !== "object" || Array.isArray(mt)) return null;
    const m = mt as Record<string, unknown>;
    const mode = MODES.includes(m.mode as MapTacticalMode) ? (m.mode as MapTacticalMode) : null;
    if (!mode) return null;
    const route = Array.isArray(m.route)
      ? (m.route as LatLng[]).filter((p) => p && typeof p.lat === "number")
      : [];
    return {
      mode,
      phase: String(m.phase ?? "waiting"),
      suspectProgress: Number(m.suspectProgress) || 0,
      route,
      escapeZone: m.escapeZone as MapTacticalState["escapeZone"],
      placements: Array.isArray(m.placements) ? (m.placements as MapTacticalPlacement[]) : [],
      resolved: m.resolved === true,
      outcome: typeof m.outcome === "string" ? m.outcome : undefined,
      heloActive: m.heloActive === true,
      stagingUnlocked: m.stagingUnlocked === true,
      label: typeof m.label === "string" ? m.label : undefined,
      maxPlacements:
        typeof m.maxPlacements === "object" && m.maxPlacements && !Array.isArray(m.maxPlacements)
          ? (m.maxPlacements as Partial<Record<PlacementType, number>>)
          : undefined,
    };
  } catch {
    return null;
  }
}

export function inferMapTacticalMode(incident: {
  templateId?: string | null;
  title: string;
  summary?: string | null;
}): MapTacticalMode | null {
  const tid = incident.templateId ?? "";
  const blob = `${incident.title} ${incident.summary ?? ""}`;
  if (PURSUIT_TEMPLATES.has(tid) || PURSUIT_TITLE.test(blob)) return "pursuit";
  if (WATER_RESCUE_TEMPLATES.has(tid) || WATER_RESCUE_TITLE.test(blob + tid)) {
    return "water_rescue";
  }
  if (RD_STAGING_TEMPLATES.has(tid) || RD_STAGING_TITLE.test(blob + tid)) return "rd_staging";
  if (WATER_TEMPLATES.has(tid) || WATER_TITLE.test(blob + tid)) return "water";
  if (BARRIER_TEMPLATES.has(tid) || BARRIER_TITLE.test(blob)) return "barrier";
  if (LAGE_TITLE.test(blob + tid) || tid === "school_evacuation" || tid === "motorway_crash") {
    return "lage";
  }
  if (/Großlage|Volksfest|Scheune|requiresStaging/i.test(blob + tid)) return "staging";
  return null;
}

export function mapTacticalHold(supportJson?: string | null): boolean {
  const mt = parseMapTactical(supportJson);
  if (!mt || mt.resolved) return false;
  if (
    mt.mode === "staging" ||
    mt.mode === "rd_staging" ||
    mt.mode === "lage" ||
    mt.mode === "water_rescue" ||
    mt.mode === "barrier" ||
    mt.mode === "water"
  ) {
    return !mt.stagingUnlocked;
  }
  if (mt.mode === "pursuit") {
    if (mt.phase === "waiting") return false;
    return mt.phase !== "resolved" && mt.phase !== "failed_soft";
  }
  return false;
}

export function suspectPosition(mt: MapTacticalState | null): LatLng | null {
  if (!mt?.route?.length) return null;
  return interpolateRoute(mt.route, mt.suspectProgress);
}

export function formatMapTacticalHint(incident: {
  templateId?: string | null;
  title: string;
  summary?: string | null;
  supportJson?: string | null;
}): string | null {
  const mt = parseMapTactical(incident.supportJson);
  if (mt?.label) return mt.label;
  const mode = mt?.mode ?? inferMapTacticalMode(incident);
  const pressureFail = parseHydrantPressureFail(incident.supportJson);
  if (mode === "pursuit") return "Verfolgungslage — Maßnahmen auf der Karte setzen";
  if (mode === "lage") return "Lage — Sammelstelle, Abschnitte, Sperren, Evakuierung";
  if (mode === "staging") return "Großlage — Kräfte-Sammelstelle auf der Karte";
  if (mode === "rd_staging") return "MANV — Patienten-Sammelstelle und Abschnitte";
  if (mode === "water") {
    return pressureFail
      ? "Wasser — Druckmangel: Saugstelle oder Pendel auf der Karte"
      : "Wasser — Hydrant oder Saugstelle auf der Karte";
  }
  if (mode === "water_rescue") {
    return "Wasserrettung — Sammelstelle, Abschnitt, Saugstelle";
  }
  if (mode === "barrier") return "Absperrung — Sperrstellen und Verkehrskontrolle";
  return null;
}

export function distanceLabel(a: LatLng, b: LatLng): string {
  const m = haversineMeters(a, b);
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

/** Kreis-GeoJSON für Evakuierungsbereich auf der Karte (Radius in km). */
export function zoneCircleGeo(lng: number, lat: number, radiusKm: number) {
  const points: number[][] = [];
  const n = 48;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    const dx = (radiusKm / (111 * Math.cos((lat * Math.PI) / 180))) * Math.cos(a);
    const dy = (radiusKm / 111) * Math.sin(a);
    points.push([lng + dx, lat + dy]);
  }
  return {
    type: "Feature" as const,
    geometry: { type: "Polygon" as const, coordinates: [points] },
    properties: {},
  };
}

export const EVACUATION_ZONE_RADIUS_KM = 0.28;
export const SECTOR_BOUNDARY_RADIUS_KM = 0.12;
export const PATIENT_STAGING_RADIUS_KM = 0.08;

/** On-Scene-Fahrzeuge für Einheit postieren (Pol/Streife). */
export function unitPostCandidates(
  vehicles: Array<{
    id: string;
    incidentId?: string | null;
    status: string;
    type: string;
    callsign?: string;
  }>,
  incidentId: string,
) {
  return vehicles.filter(
    (v) =>
      v.incidentId === incidentId &&
      v.status === "onscene" &&
      /FuStW|Pol|BAB-FuStW/i.test(v.type),
  );
}
